import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Close as CloseIcon, DeleteOutline as DeleteIcon, EditOutlined as EditIcon } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import ScienceIcon from "@mui/icons-material/Science";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import DownloadIcon from "@mui/icons-material/CloudDownloadOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import useFetch from "../../hooks/useFetch";
import SearchInput from "../../components/SearchInput";
import ArrayChipInput from "../../components/ArrayChipInput";
import LoadingSpinner from "../../components/LoadingSpinner";
import TrimTextField from "../../components/TrimTextField";
import { INDIGO, SLATE } from "../../constants/Colors";
import IntentTesterSidebar from "./IntentTesterSidebar";
import PushToEnvironmentDialog from "./PushToEnvironmentDialog";
import ImportIntentsSidebar from "./ImportIntents";
import TestRunsDialog from "./TestRunsDialog";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const COL_HEADER_SX = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: SLATE[500],
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const EMPTY_FORM = {
  intent_group: "",
  intent_name: "",
  intent_display_name: "",
  intent_description: "",
  tags: "[]",
};

export default function IntentModelsList() {
  const tokens = useSelector((state) => state?.tokens.value);
  const { setPageMeta } = usePageMeta();

  const [refreshKey, setRefreshKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [testerOpen, setTesterOpen] = useState(false);
  const [testRunsOpen, setTestRunsOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [reopenImportOnSave, setReopenImportOnSave] = useState(false);

  // Form sidebar state
  const [formOpen, setFormOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null); // null = create mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const { data, loading } = useFetch(`/v1/intent_models`, tokens, [refreshKey], {});
  const { makeRequest } = useApiCall();

  const intentModels = useMemo(() => {
    return (data?.intent_models || []).map((m) => ({ ...m, tags: m.tags || [] }));
  }, [data]);

  const filteredModels = useMemo(() => {
    setPage(0);
    if (!searchQuery.trim()) return intentModels;
    const q = searchQuery.toLowerCase();
    return intentModels.filter((m) =>
      m.intent_group?.toLowerCase().includes(q) ||
      m.intent_name?.toLowerCase().includes(q) ||
      m.intent_display_name?.toLowerCase().includes(q) ||
      (m.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [intentModels, searchQuery]);

  const pagedModels = useMemo(
    () => filteredModels.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredModels, page, rowsPerPage]
  );

  const openCreate = () => {
    setAddMenuAnchor(null);
    setEditingModel(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setReopenImportOnSave(false);
    setFormOpen(true);
  };

  const openEdit = (model) => {
    setEditingModel(model);
    setForm({
      intent_group: model.intent_group || "",
      intent_name: model.intent_name || "",
      intent_display_name: model.intent_display_name || "",
      intent_description: model.intent_description || "",
      tags: JSON.stringify(model.tags || []),
    });
    setFormErrors({});
    setReopenImportOnSave(false);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingModel(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const openImport = () => {
    setAddMenuAnchor(null);
    setImportOpen(true);
  };

  const handleGenerated = (generated, market) => {
    const tags = Array.from(new Set([...(generated.tags || []), market].filter(Boolean)));
    setEditingModel(null);
    setForm({
      intent_group: generated.intent_group || "",
      intent_name: generated.intent_name || "",
      intent_display_name: generated.intent_display_name || "",
      intent_description: generated.intent_description || "",
      tags: JSON.stringify(tags),
    });
    setFormErrors({});
    setReopenImportOnSave(true);
    setFormOpen(true);
  };

  const handleSave = async () => {
    const errors = {};
    if (!form.intent_name.trim()) errors.intent_name = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    let tags = [];
    try { tags = JSON.parse(form.tags || "[]"); } catch { tags = []; }

    const payload = {
      intent_name: form.intent_name.trim(),
      intent_group: form.intent_group.trim(),
      intent_display_name: form.intent_display_name.trim(),
      intent_description: form.intent_description.trim(),
      tags,
    };

    setSaving(true);
    try {
      if (editingModel) {
        await makeRequest(`/v1/intent_models/${editingModel.id}`, {
          method: "PUT",
          body: { intent_model: payload },
          authTokens: tokens,
        });
      } else {
        await makeRequest(`/v1/intent_models`, {
          method: "POST",
          body: { intent_model: payload },
          authTokens: tokens,
        });
      }
      setRefreshKey((k) => k + 1);
      handleFormClose();
      if (reopenImportOnSave) {
        setReopenImportOnSave(false);
        setImportOpen(true);
      }
    } catch {
      // error already toasted
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (model) => {
    if (!window.confirm(`Delete intent "${model.intent_name}"?`)) return;
    try {
      await makeRequest(`/v1/intent_models/${model.id}`, {
        method: "DELETE",
        authTokens: tokens,
      });
      setRefreshKey((k) => k + 1);
    } catch {
      // error already toasted
    }
  };

  useEffect(() => {
    setPageMeta({
      description: "Define and manage intent models for AI classification.",
      actions: (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => setPushOpen(true)}
            sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none" }}
          >
            Push
          </Button>
          <Button
            variant="outlined"
            startIcon={<ScienceIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => setTesterOpen(true)}
            sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none" }}
          >
            Test Intent
          </Button>
          <Button
            variant="outlined"
            startIcon={<CompareArrowsIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => setTestRunsOpen(true)}
            sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none" }}
          >
            Test Models
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            disabled={loading}
            sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
          >
            Add Intent
          </Button>
        </Stack>
      ),
    });
    return () => setPageMeta({});
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 2.5 }}>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search intent models..."
        />
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading intent models..." />
      ) : (
        <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", overflow: "hidden" }}>
          {/* Column headers */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "140px 0.8fr 0.7fr 260px 80px",
              px: 3,
              py: 1.5,
              borderBottom: `1px solid ${SLATE[200]}`,
              bgcolor: SLATE[50],
            }}
          >
            <Typography sx={COL_HEADER_SX}>Group</Typography>
            <Typography sx={COL_HEADER_SX}>Intent Name</Typography>
            <Typography sx={COL_HEADER_SX}>Display Name</Typography>
            <Typography sx={COL_HEADER_SX}>Tags</Typography>
            <Typography sx={{ ...COL_HEADER_SX, textAlign: "right" }}>Actions</Typography>
          </Box>

          {filteredModels.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>🧠</Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
                No intent models yet
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[500], mb: 2.5 }}>
                Add your first intent to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreate}
                sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
              >
                Add Intent
              </Button>
            </Box>
          ) : (
            pagedModels.map((model, idx) => (
              <Box
                key={model.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "140px 0.8fr 0.7fr 260px 80px",
                  px: 3,
                  py: 1.5,
                  alignItems: "center",
                  borderBottom: idx < pagedModels.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                  "&:hover": { bgcolor: SLATE[50] },
                }}
              >
                <Typography sx={{ fontSize: "0.8125rem", color: SLATE[600], pr: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {model.intent_group || "—"}
                </Typography>
                <Box sx={{ pr: 2, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: SLATE[900], whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {model.intent_name}
                  </Typography>
                  {model.intent_description && (
                    <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {model.intent_description}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: "0.8125rem", color: SLATE[600], pr: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {model.intent_display_name || "—"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pr: 2 }}>
                  {(model.tags || []).slice(0, 4).map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{ bgcolor: INDIGO[50], color: INDIGO[700], borderRadius: "6px", fontSize: "0.75rem", height: "22px" }}
                    />
                  ))}
                  {(model.tags || []).length > 4 && (
                    <Chip
                      label={`+${model.tags.length - 4}`}
                      size="small"
                      sx={{ bgcolor: SLATE[100], color: SLATE[500], borderRadius: "6px", fontSize: "0.75rem", height: "22px" }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                  <Tooltip title="Edit" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => openEdit(model)}
                      sx={{ color: SLATE[400], "&:hover": { color: INDIGO[600], bgcolor: "transparent" } }}
                    >
                      <EditIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(model)}
                      sx={{ color: SLATE[400], "&:hover": { color: "#ef4444", bgcolor: "transparent" } }}
                    >
                      <DeleteIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))
          )}

          {!loading && filteredModels.length > 0 && (
            <Box sx={{ borderTop: `1px solid ${SLATE[200]}`, bgcolor: "#ffffff" }}>
              <TablePagination
                component="div"
                count={filteredModels.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{
                  "& .MuiTablePagination-toolbar": { minHeight: 48 },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.8125rem", color: SLATE[600] },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Add Intent menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={openCreate}>
          <ListItemIcon><NoteAddOutlinedIcon sx={{ fontSize: "1.1rem", color: SLATE[600] }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>New Intent</ListItemText>
        </MenuItem>
        <MenuItem onClick={openImport}>
          <ListItemIcon><DownloadIcon sx={{ fontSize: "1.1rem", color: SLATE[600] }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>Import Intent</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit / Create sidebar */}
      <Drawer
        anchor="right"
        open={formOpen}
        onClose={handleFormClose}
        sx={{ zIndex: 21474834 }}
        PaperProps={{
          sx: {
            width: "40%",
            minWidth: 480,
            maxWidth: 600,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: SLATE[50],
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            bgcolor: "#ffffff",
            borderBottom: `1px solid ${SLATE[200]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <NoteAddOutlinedIcon sx={{ fontSize: "1.25rem", color: INDIGO[500] }} />
            <Box>
              <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.08em", mb: "2px" }}>
                AI Models
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: SLATE[900] }}>
                {editingModel ? "Edit Intent Model" : "Add Intent Model"}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleFormClose} sx={{ color: SLATE[400], "&:hover": { color: SLATE[700], bgcolor: SLATE[100] } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Intent Name <span style={{ color: "#ef4444" }}>*</span></Typography>
            <TrimTextField
              fullWidth size="small" placeholder="e.g. book_appointment"
              value={form.intent_name}
              onChange={(e) => { setForm((p) => ({ ...p, intent_name: e.target.value })); setFormErrors((p) => ({ ...p, intent_name: false })); }}
              error={formErrors.intent_name}
              helperText={formErrors.intent_name ? "Intent name is required" : ""}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Group</Typography>
            <TrimTextField
              fullWidth size="small" placeholder="e.g. appointments"
              value={form.intent_group}
              onChange={(e) => setForm((p) => ({ ...p, intent_group: e.target.value }))}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Display Name</Typography>
            <TrimTextField
              fullWidth size="small" placeholder="e.g. Book an Appointment"
              value={form.intent_display_name}
              onChange={(e) => setForm((p) => ({ ...p, intent_display_name: e.target.value }))}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Description</Typography>
            <TextField
              fullWidth multiline rows={5} size="small"
              placeholder="Describe what this intent represents…"
              value={form.intent_description}
              onChange={(e) => setForm((p) => ({ ...p, intent_description: e.target.value }))}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Tags</Typography>
            <ArrayChipInput
              value={form.tags}
              onChange={(val) => setForm((p) => ({ ...p, tags: val }))}
              placeholder="Type and press Enter to add tags…"
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button
              onClick={handleFormClose}
              variant="outlined" size="small"
              sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              variant="contained" size="small"
              data-testid="intent-form-save-button"
              sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
            >
              {saving && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
              {editingModel ? "Save Changes" : "Add Intent"}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <IntentTesterSidebar
        isOpen={testerOpen}
        onClose={() => setTesterOpen(false)}
        tokens={tokens}
      />

      <ImportIntentsSidebar
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        tokens={tokens}
        onGenerated={handleGenerated}
      />

      <PushToEnvironmentDialog
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        tokens={tokens}
      />

      <TestRunsDialog
        open={testRunsOpen}
        onClose={() => setTestRunsOpen(false)}
        tokens={tokens}
      />
    </Box>
  );
}
